import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, KeyRound, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!token) setError("Link non valido: token mancante");
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (pw.length < 6) {
            setError("La password deve avere almeno 6 caratteri");
            return;
        }
        if (pw !== pw2) {
            setError("Le password non coincidono");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            setDone(true);
            toast.success("Password aggiornata");
            setTimeout(() => navigate("/login"), 2500);
        } catch (e) {
            setError(e.response?.data?.detail || "Errore aggiornamento password");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="relative min-h-screen overflow-hidden" data-testid="reset-password-page">
            <img
                src="/brand/login-bg.webp"
                alt=""
                className="absolute inset-0 h-full w-full object-cover brightness-110 saturate-75"
            />
            <div className="absolute inset-0 bg-[#376DB3]/65" />
            <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white/95 p-8 shadow-2xl backdrop-blur-sm sm:p-10">
                    <div className="flex justify-center">
                        <img src="/brand/logo-full.jpg" alt="Splendor Sud" className="h-24 w-auto" />
                    </div>

                    <h1 className="mt-6 flex items-center justify-center gap-2 text-center font-display text-2xl font-bold text-slate-900">
                        <KeyRound className="h-6 w-6 text-[#376DB3]" />
                        Nuova password
                    </h1>
                    <p className="mt-2 text-center text-sm text-slate-600">
                        Imposta una nuova password per accedere al portale HR.
                    </p>

                    {done ? (
                        <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="reset-success">
                            <div className="flex items-center gap-2 font-medium">
                                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                Password aggiornata correttamente
                            </div>
                            <p className="mt-1 text-xs">Reindirizzamento al login…</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                            <div>
                                <Label htmlFor="pw">Nuova password</Label>
                                <Input
                                    id="pw"
                                    type="password"
                                    value={pw}
                                    onChange={(e) => setPw(e.target.value)}
                                    placeholder="Almeno 6 caratteri"
                                    required
                                    autoFocus
                                    autoComplete="new-password"
                                    className="mt-1.5"
                                    data-testid="reset-pw-1"
                                />
                            </div>
                            <div>
                                <Label htmlFor="pw2">Conferma password</Label>
                                <Input
                                    id="pw2"
                                    type="password"
                                    value={pw2}
                                    onChange={(e) => setPw2(e.target.value)}
                                    placeholder="Reinserisci la password"
                                    required
                                    autoComplete="new-password"
                                    className="mt-1.5"
                                    data-testid="reset-pw-2"
                                />
                            </div>

                            {error && (
                                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-error">
                                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={submitting || !token}
                                className="w-full gap-2 bg-[#376DB3] py-6 text-base font-medium text-white shadow-md hover:bg-[#376DB3]/90"
                                data-testid="reset-submit-btn"
                            >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                                Aggiorna password
                            </Button>
                        </form>
                    )}

                    <div className="mt-6 text-center">
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#376DB3] hover:underline"
                            data-testid="reset-back-login"
                        >
                            <ArrowLeft className="h-3 w-3" />
                            Torna al login
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
